import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { map, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
  Bundle,
  PurchaseBundleEntry,
  TransactionDetails,
  BundleSummary,
  UpdateBranchPlansInput,
  BundleTransaction,
  StripePlan,
} from '../types';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private apiHost = `${environment.apiBaseUrl}`;
  private theftPlans = {};

  private httpClient = inject(HttpClient);

  getTheftPlanName(priceId: string, customerType: string) {
    if (this.theftPlans[customerType] === undefined) {
      return this.getTheftPlans(customerType).pipe(
        map(() => {
          const price = this.theftPlans[customerType].find((plan) => plan.id === priceId);
          return price?.product?.name;
        }),
      );
    }

    return of(this.theftPlans[customerType].find((plan) => plan.id === priceId)?.product?.name);
  }

  getTheftPlans(customerType: string): Observable<StripePlan[]> {
    if (this.theftPlans[customerType] !== undefined) {
      return of(this.theftPlans[customerType]);
    }

    const requestUrl = `${this.apiHost}/Subscription/TheftPlans/${customerType}`;
    return this.httpClient.get<StripePlan[]>(requestUrl).pipe(
      tap((response) => {
        this.theftPlans[customerType] = response;
      }),
    );
  }

  getBundles(customerType: string) {
    const requestUrl = `${this.apiHost}/Subscription/Bundles/${customerType}`;
    return this.httpClient.get<Bundle[]>(requestUrl);
  }

  getProducts() {
    const requestUrl = `${this.apiHost}/Subscription/Products`;
    return this.httpClient.get<unknown[]>(requestUrl);
  }

  updateSubscription(input: UpdateBranchPlansInput) {
    const requestUrl = `${this.apiHost}/Subscription/UpdateBranchPlans/${input.branch_id}`;
    return this.httpClient.post<unknown>(requestUrl, input);
  }

  toggleFlagLocation(location_id: number, flagged_reasons?: string[]) {
    const requestUrl = `${this.apiHost}/Subscription/ToggleFlagLocation/${location_id}`;
    return this.httpClient.post<unknown>(requestUrl, { flagged_reasons });
  }

  getBundleTransactionHistory(branch_id: number) {
    const requestUrl = `${this.apiHost}/Subscription/BundleTransactionHistory/${branch_id}`;
    return this.httpClient.get<BundleTransaction[]>(requestUrl);
  }

  purchaseBundles(branch_id: number, bundles: PurchaseBundleEntry[], transaction_details: TransactionDetails) {
    const requestUrl = `${this.apiHost}/Subscription/PurchaseBundles`;
    return this.httpClient.post<unknown>(requestUrl, {
      branch_id,
      bundles,
      transaction_details,
    });
  }

  purchaseProducts(branch_id: number, products: any[]) {
    const requestUrl = `${this.apiHost}/Subscription/PurchaseProducts`;
    const productsInput = {};
    products.forEach((p) => {
      productsInput[p.default_price.id] = p.quantity;
    });
    return this.httpClient.post<unknown>(requestUrl, {
      branch_id,
      products: productsInput,
    });
  }

  getBundleSummary(branch_id: number) {
    const requestUrl = `${this.apiHost}/Subscription/BundleSummary/${branch_id}`;
    return this.httpClient.get<BundleSummary[]>(requestUrl);
  }
}
